import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useInvestorDetailStore from '../stores/investorDetailStore';
import useTenantDetailStore from '../stores/tenantDetailStore';

type AllowedUserType = 'investor' | 'tenant' | 'both';

interface ProtectedRouteProps {
    children: React.ReactNode;
    allowedUserTypes?: AllowedUserType;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    allowedUserTypes = 'both' // Default to investor for backward compatibility
}) => {
    const location = useLocation();

    // Stores
    const { investor, isLoading: investorLoading, isInitialLoaded: investorLoaded } = useInvestorDetailStore();
    const { tenant, isLoading: tenantLoading, isInitialLoaded: tenantLoaded } = useTenantDetailStore();

    // Determine if authentication is still loading
    const isAuthLoading = investorLoading || tenantLoading;
    const isAuthLoaded = investorLoaded || tenantLoaded;

    // If authentication is still loading and hasn't been loaded yet, show loading
    if (isAuthLoading && !isAuthLoaded) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // Determine if user is authenticated and what type they are
    const isInvestorAuthenticated = !!investor;
    const isTenantAuthenticated = !!tenant;
    const isAuthenticated = isInvestorAuthenticated || isTenantAuthenticated;

    // Check if the current user type is allowed for this route
    const isUserTypeAllowed = () => {
        if (allowedUserTypes === 'both') {
            return isAuthenticated;
        }
        if (allowedUserTypes === 'investor') {
            return isInvestorAuthenticated;
        }
        if (allowedUserTypes === 'tenant') {
            return isTenantAuthenticated;
        }
        return false;
    };

    // If not authenticated or user type not allowed, redirect to login
    if (!isUserTypeAllowed()) {
        return <Navigate to="/login" replace />;
    }

    // Handle investor-specific onboarding logic
    if (isInvestorAuthenticated && investor.onboarding_step < 4 && location.pathname !== "/onboarding") {
        return <Navigate to="/onboarding" replace />;
    }

    if (isTenantAuthenticated && tenant.onboarding_step < 2 && location.pathname !== "/onboarding") {
        return <Navigate to="/onboarding" replace />;
    }

    // Handle tenant-specific logic if needed
    // You can add tenant-specific navigation logic here in the future

    return <>{children}</>;
};

export default ProtectedRoute;
